import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { processTicketsForComparison, buildAlignedArrays, buildSingleEventData } from "../helpers/processTickets";
import { buildChartOptions } from "../helpers/chartOptions";
import { COMPARISON_COLORS } from "../helpers/constants";
import { dateRangeToOffsets } from "../helpers/dateRangeUtils";

export function useChartData({
  tickets, compareTickets, eventName, compareEventName, eventDays,
  dateFrom, dateTo, eventStartDate, compareEventStartDate,
}) {
  const { t } = useTranslation();
  const isComparing = compareTickets?.length > 0;

  const chartData = useMemo(() => {
    if (!tickets?.length) return null;

    if (isComparing) {
      // Process both events relative to their own event start date
      const primary = processTicketsForComparison(tickets, eventStartDate);
      const compare = processTicketsForComparison(compareTickets, compareEventStartDate);

      // Determine the combined offset range so both events align
      const dataMin = Math.min(primary.minOffset, compare.minOffset);
      const dataMax = Math.max(primary.maxOffset, compare.maxOffset);

      // Default: 90-day window before event start (offset 0), unless user set a custom range
      let rangeStart = dateFrom ? dataMin : Math.max(dataMin, -90);
      let rangeEnd = dateTo ? dataMax : Math.min(dataMax, Math.max(primary.maxOffset, compare.maxOffset, 10));

      // Apply date range filter (dates map to offsets via primary event start)
      if ((dateFrom || dateTo) && eventStartDate) {
        const { startOffset, endOffset } = dateRangeToOffsets(eventStartDate, dateFrom, dateTo);
        if (startOffset !== null) rangeStart = Math.max(dataMin, startOffset);
        if (endOffset !== null) rangeEnd = Math.min(dataMax, endOffset);
      }

      if (rangeStart > rangeEnd) return null;

      const primaryData = buildAlignedArrays(primary.offsetCounts, rangeStart, rangeEnd);
      const compareData = buildAlignedArrays(compare.offsetCounts, rangeStart, rangeEnd);
      const maxLen = rangeEnd - rangeStart + 1;

      // Build labels: "T-30", "T-29", ..., "T-1", "Start", "Tag 1", "Tag 2", ...
      const labels = Array.from({ length: maxLen }, (_, i) => {
        const offset = rangeStart + i;
        if (offset < 0) return `T${offset}`;
        if (offset === 0) return t("Event start");
        return `${t("Day")} ${offset}`;
      });

      const col = COMPARISON_COLORS;

      return {
        labels,
        datasets: [
          {
            label: `${eventName} - ${t("Daily tickets")}`,
            data: primaryData.daily,
            borderColor: col.primary.daily.border,
            backgroundColor: col.primary.daily.background,
            yAxisID: "y",
            fill: true,
            tension: 0.3,
            borderWidth: 2.5,
            pointRadius: maxLen > 60 ? 0 : 4,
            pointHoverRadius: 6,
          },
          {
            label: `${eventName} - ${t("Cumulative")}`,
            data: primaryData.cumulative,
            borderColor: col.primary.cumulative.border,
            backgroundColor: col.primary.cumulative.background,
            yAxisID: "y1",
            fill: false,
            tension: 0.3,
            borderWidth: 2,
            pointRadius: maxLen > 60 ? 0 : 4,
            pointHoverRadius: 6,
            borderDash: [8, 4],
          },
          {
            label: `${compareEventName} - ${t("Daily tickets")}`,
            data: compareData.daily,
            borderColor: col.compare.daily.border,
            backgroundColor: col.compare.daily.background,
            yAxisID: "y",
            fill: true,
            tension: 0.3,
            borderWidth: 2.5,
            pointRadius: maxLen > 60 ? 0 : 4,
            pointHoverRadius: 6,
            pointStyle: "rectRot",
          },
          {
            label: `${compareEventName} - ${t("Cumulative")}`,
            data: compareData.cumulative,
            borderColor: col.compare.cumulative.border,
            backgroundColor: col.compare.cumulative.background,
            yAxisID: "y1",
            fill: false,
            tension: 0.3,
            borderWidth: 2,
            pointRadius: maxLen > 60 ? 0 : 4,
            pointHoverRadius: 6,
            pointStyle: "rectRot",
            borderDash: [4, 4],
          },
        ],
      };
    }

    return buildSingleEventData(tickets, eventDays, t, { dateFrom, dateTo });
  }, [tickets, compareTickets, eventName, compareEventName, isComparing, eventDays, dateFrom, dateTo, eventStartDate, compareEventStartDate, t]);

  const options = useMemo(
    () => buildChartOptions({ t, isComparing, eventName, compareEventName }),
    [t, isComparing, eventName, compareEventName]
  );

  return { chartData, options, isComparing };
}
